import { ChatUser, FileIconStyle, FileIconV2Style } from '@equip.health/ui';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { BaseMessage, FileMessage, UserMessage } from '@sendbird/chat/message';
import { difference, find, findLast, isEmpty } from 'lodash';
import { DateTime } from 'luxon';

import {
  ALWAYS_ALLOWED_CHANNEL_LIST,
  CHAT_ATTACHMENT_SIZE_LIMIT,
  FROZEN,
  FROZEN_MESSAGE,
  INTERNAL_CHANNEL_URL_PREFIXES,
  HERE_MEMBER,
  IN_TREATMENT_CHANNEL_LIST,
  MIME_TYPES,
  RESTRICTED_USERS_FROM_TAGGING,
  UNFROZEN,
  UNFROZEN_MESSAGE,
  USER_ACTIONS,
  UserRoles,
  DEFAULT_REPLY_PLACEHOLDER,
  MENTION_PEOPLE_TO_NOTIFY_THEM_OF_YOUR_MESSAGE,
  TYPE_YOUR_MESSAGE_TO_THE_GROUP_HERE,
} from '~/lib/constants/chat.constants';
import {
  MESSAGE_FILE_PROCESSING,
  MESSAGE_INVALID_FILE,
  S3FileStatus,
} from '~/lib/constants/s3filestatus.contants';

import {
  ChatMessage,
  ChatMessageProps,
  EquipUserType,
  MemberMetaData,
  SplitGroupChannels,
  TaggingUser,
  UnreadMessagesInfo,
  UserExtended,
} from '../../types/chat';
import StringUtil from '../string.util';
import { User } from '@sendbird/chat';
import { PatientUniversalStatusCode } from '~/lib/constants/patients.constants';

const ChatUtil = {
  // a. if created date is 0-2 days ago, returns relative date (e.g. "yesterday", "today")
  // b. else if created date was in the same year, returns "{dayOfWeek}, {month} {day}"
  // c. else if created date was in any other year, returns "{month} {day}, {year}"
  calculateRelativeDate: (
    createdDate: number,
    ignoreWeekDay = false,
  ): string => {
    const incomingDate = DateTime.fromMillis(createdDate);
    const today = DateTime.now();

    if (
      [
        today.toLocaleString(),
        today.minus({ days: 1 }).toLocaleString(),
      ].includes(incomingDate.toLocaleString())
    ) {
      return incomingDate.toRelativeCalendar({ unit: 'days' });
    }

    if (incomingDate.year === today.year) {
      return incomingDate
        .toFormat(ignoreWeekDay ? 'LLLL d' : 'cccc, LLLL d')
        .replace(today.year.toFixed(), '');
    }

    return incomingDate.toFormat('LLLL d, yyyy');
  },

  excludeCurrentUserId: (
    memberIds: string[],
    currentUserId: string,
  ): string[] => {
    return memberIds.filter((x) => x !== currentUserId);
  },

  expandHere: (memberIds: string[], mentionedUserIds: string[]): string[] => {
    if ((mentionedUserIds ?? []).includes(HERE_MEMBER.userId)) {
      return memberIds;
    }

    return mentionedUserIds;
  },

  findMessageIndex: (
    messages: ChatMessage[],
    messageIdToFind: number,
  ): number => {
    if (messages)
      return messages.findIndex((x) => x.messageId === messageIdToFind);

    return -1;
  },

  getAllMentionedUserIds: (messages: ChatMessage[]): string[] => {
    const output: string[] = [];

    if (messages) {
      messages.forEach((message) =>
        output.push(...(message.allMentionedUserIds ?? [])),
      );

      return [...new Set(output)];
    }

    return [];
  },

  getMessagesLength: (items: Array<ChatMessage>): number => {
    return items ? items.length : 0;
  },

  getRepliesText: (replyCount?: number): string => {
    let output = '';

    switch (replyCount ?? 0) {
      case 0:
        break;

      case 1:
        output = `${replyCount} reply`;
        break;

      default:
        output = `${replyCount} replies`;
        break;
    }

    return output;
  },

  getUrl: (input: string): string => {
    try {
      const url = new URL(input);

      return url.href;
    } catch {
      return `https://${input}`;
    }
  },

  getUserIds: (channelMembers: ChatUser[]): string[] => {
    return (channelMembers ?? []).map((x) => x.userId);
  },

  isChannelMember: (memberIds: string[], currentUserId: string): boolean => {
    return (memberIds ?? []).includes(currentUserId);
  },

  isValidUrl: (urlString: string): boolean => {
    if (!isEmpty(urlString)) {
      try {
        const url = new URL(urlString);

        return (
          !isEmpty(url.origin) &&
          (urlString.startsWith('http') || urlString.startsWith('https'))
        );
      } catch {
        return false;
      }
    }

    return false;
  },

  mergeMessages: (
    oldMessages: ChatMessage[],
    newMessages: ChatMessage[],
  ): ChatMessage[] => {
    const oldMessageIds = (oldMessages ?? []).map((x) => x.messageId);
    const messages = (newMessages ?? []).filter(
      (message) => !oldMessageIds.includes(message.messageId),
    );
    return [...new Set((oldMessages ?? []).concat(messages))].sort(
      (a, b) => a.createdAt - b.createdAt,
    );
  },

  replaceAll: (
    originalString: string,
    findString: string,
    replaceWith: string,
  ): string => {
    const searchResults = (originalString ?? '').match(
      new RegExp(StringUtil.escapeRegExp(findString), 'gi'),
    );

    let output: string = originalString ?? '';
    if (searchResults) {
      for (const result of searchResults) {
        output = StringUtil.replaceAll(
          output,
          result,
          replaceWith.replace('#', result),
        );
      }
    }

    return output;
  },

  sortByCreatedDate: (messages: ChatMessage[]): ChatMessage[] => {
    if (messages) return messages.sort((a, b) => a.createdAt - b.createdAt);

    return [];
  },

  updateMessage: (
    message: ChatMessage,
    updates: ChatMessageProps,
  ): ChatMessage => {
    return { ...message, ...updates };
  },
};

export const filterChannelMembers = (
  userIds: string[],
  channelMembers: ChatUser[],
): string[] => {
  const output = [];
  const channelMemberIds = channelMembers.map((member) => member.userId);
  for (const userId of userIds) {
    if (ChatUtil.isChannelMember(channelMemberIds, userId)) {
      output.push(userId);
    }
  }
  return output;
};

export const getUserActionIndex = (message: string): number => {
  if (message) {
    return USER_ACTIONS.findIndex((action) => message.includes(action));
  }

  return -1;
};

/// Extract user from admin messages if the message contains user actions of joining or leaving the channel
export const getUserFromAdminMessage = (message: string): UserExtended => {
  const userActionIndex = getUserActionIndex(message);
  const nickname =
    userActionIndex > -1
      ? message.replace(` ${USER_ACTIONS[userActionIndex]}`, '')
      : '';

  return {
    friendName: nickname,
    isActive: true,
    metaData: {},
    nickname,
    userId: '0',
  } as unknown as UserExtended;
};

export const getAdminMessageText = (
  message: string,
  senderNickname: string,
): string => {
  if (!message) return null;

  if (message.endsWith(UNFROZEN)) return UNFROZEN_MESSAGE;
  if (message.endsWith(FROZEN)) return FROZEN_MESSAGE;

  if (senderNickname) return message.replace(senderNickname, 'Has');

  return message;
};

export const isExternalChannel = (channelUrl: string): boolean => {
  if (channelUrl) {
    return !INTERNAL_CHANNEL_URL_PREFIXES.some((prefix) =>
      channelUrl.startsWith(prefix),
    );
  }

  return false;
};

export const getUnreadMessagesCount = (c: GroupChannel) =>
  isExternalChannel(c.url)
    ? c.unreadMessageCount
    : c.totalUnreadReplyCount + c.unreadMentionCount;

export const getAllowedChannels = (
  userChannels: GroupChannel[],
  isPatientInTreatment?: boolean,
): GroupChannel[] =>
  (userChannels ?? []).filter((channel) =>
    ALWAYS_ALLOWED_CHANNEL_LIST.concat(
      isPatientInTreatment ? IN_TREATMENT_CHANNEL_LIST : [],
    ).some((allowedChannel) => channel.url.startsWith(allowedChannel)),
  );

export const getNonMembershipChannels = (
  userChannels: GroupChannel[],
  currentUserId: string,
): GroupChannel[] => {
  const nonMemberChannels = userChannels.filter(
    (channel) =>
      channel.members?.length === 0 ||
      !channel.members.some((m) => m.userId === currentUserId),
  );
  return nonMemberChannels;
};

export const getNotifyUserIds = (
  message: ChatMessage,
  currentUserId: string,
  mentionedUserIds: string[],
  parentMessageUserIds: string[],
  channelMembers: ChatUser[],
): string[] => {
  const output: string[] = [] as string[];

  if (message && message.thread?.length > 0 && channelMembers) {
    const { thread } = message;
    const excludeUserIds = [...(mentionedUserIds ?? [])];
    excludeUserIds.push(HERE_MEMBER.userId, currentUserId);
    let threadMemberUserIds: string[] = [...(message.mentionedUserIds ?? [])];

    threadMemberUserIds.push(message.sender?.userId);
    threadMemberUserIds.push(...(parentMessageUserIds ?? []));

    thread.forEach((reply) => {
      threadMemberUserIds.push(...reply.mentionedUserIds, reply.sender?.userId);
    });

    threadMemberUserIds = [...new Set(threadMemberUserIds)];

    threadMemberUserIds = difference(threadMemberUserIds, excludeUserIds);

    const channelMemberUserIds =
      channelMembers?.map((member) => member.userId) ?? [];

    threadMemberUserIds.forEach((member) => {
      if (channelMemberUserIds.includes(member)) {
        output.push(member);
      }
    });
  } else if (parentMessageUserIds?.length > 0) {
    output.push(...parentMessageUserIds);
  } else if (channelMembers) {
    output.push(
      ...channelMembers
        .filter((member) => member.userId !== currentUserId)
        .map((member) => member.userId),
    );
  }

  return [...new Set(output)];
};

export const isInactiveRotatedChannel = (channel: GroupChannel) => {
  const maybeRotatedChannel = channel as GroupChannel & {
    isRotatedChannel: boolean;
  };
  return maybeRotatedChannel?.isRotatedChannel === true; // must be explicitly set to "true"
};

export const isReadOnlyChannel = (
  channel: GroupChannel,
  userId: string,
): boolean => {
  if (channel && userId) {
    return (
      channel?.isFrozen ||
      !(channel?.members ?? []).some((member) => member.userId === userId)
    );
  }

  return true;
};

export const isUnreadMessage = (message: ChatMessage): boolean => {
  return !message.isRead || message.hasNewReply;
};

export const getUnreadMessageInfo = (
  messages: ChatMessage[],
): UnreadMessagesInfo => {
  const totalUnreadMessages =
    messages?.length > 0
      ? messages.filter((message) => isUnreadMessage(message)).length
      : 0;
  if (totalUnreadMessages > 0) {
    return {
      firstUnreadMessageId: find(messages, (message) =>
        isUnreadMessage(message),
      ).messageId,
      lastMessageId: messages[0].messageId,
      lastUnreadMessageId: findLast(messages, (message) =>
        isUnreadMessage(message),
      ).messageId,
      totalUnreadMessages,
    };
  }

  return {
    firstUnreadMessageId: 0,
    lastMessageId: 0,
    lastUnreadMessageId: 0,
    totalUnreadMessages,
  };
};

export const getExtension = (filename: string): string => {
  return filename.split('.').pop().toLowerCase();
};

export const isImage = (filename: string): boolean => {
  const fileExtension = getExtension(filename ?? '');

  if (fileExtension) {
    const mimeType = MIME_TYPES[fileExtension] ?? '';

    return mimeType.startsWith('image');
  }

  return false;
};

export const getAllowedUsersForTagging = (
  members: ChatUser[],
  currentUserId: string,
): TaggingUser[] => {
  if (members) {
    return members
      .filter(
        (member) =>
          member?.userId !== currentUserId &&
          !RESTRICTED_USERS_FROM_TAGGING.includes(
            (
              (member?.metaData as MemberMetaData)?.userType ?? ''
            ).toLowerCase(),
          ),
      )
      .map((member) => ({ display: member?.nickname, id: member?.userId }));
  }

  return [];
};

export const getFileIcon = (filename: string): FileIconStyle => {
  if (!filename) return FileIconStyle.NONE;

  const fileExtension = getExtension(filename);

  switch (fileExtension) {
    case 'pdf':
      return FileIconStyle.PDF;

    case 'xls':
    case 'xlsx':
      return FileIconStyle.EXCEL;

    case 'doc':
    case 'docx':
      return FileIconStyle.WORD;

    case 'ppt':
    case 'pptx':
      return FileIconStyle.POWERPOINT;

    case 'mov':
    case 'avi':
    case 'mp4':
      return FileIconStyle.VIDEO;
    case 'jpg':
    case 'png':
    case 'jpeg':
    case 'tiff':
    case 'tif':
      return FileIconStyle.IMAGE;

    default:
      return FileIconStyle.NONE;
  }
};

export const getFileIconV2 = (
  filename: string,
): FileIconV2Style | undefined => {
  if (!filename) return undefined;

  const fileExtension = getExtension(filename);

  switch (fileExtension) {
    case 'pdf':
      return FileIconV2Style.PDF;

    case 'xls':
    case 'xlsx':
      return FileIconV2Style.EXCEL;

    case 'doc':
    case 'docx':
      return FileIconV2Style.WORD;

    case 'jpg':
    case 'png':
    case 'jpeg':
    case 'tiff':
    case 'tif':
    case 'avif':
    case 'webp':
    case 'bmp':
      return FileIconV2Style.IMAGE;

    default:
      return undefined;
  }
};

export const isValidFileSizeLimit = (fileSize: number): boolean => {
  const currentFileSize = Math.round((fileSize ?? 0) / 1024);

  return currentFileSize > 0 && currentFileSize <= CHAT_ATTACHMENT_SIZE_LIMIT;
};

export const filterMessages = (
  messages: ChatMessage[],
  filterText: string,
): ChatMessage[] => {
  if (messages?.length > 0 && filterText) {
    return messages.filter((attachment) =>
      (attachment?.sbMessage as FileMessage)?.name
        ?.toLowerCase()
        .includes(filterText.toLowerCase()),
    );
  }

  return messages;
};

export const removeUUIDFromFilename = (fileName: string): string => {
  const uuidPattern = /[a-f0-9]{8}-([a-f0-9]{4}-){3}[a-f0-9]{12}-/; // UUID pattern
  const match = fileName?.match(uuidPattern);

  if (match) {
    // Replace UUID with an empty string
    return fileName.replace(match[0], '');
  }

  // If not enough parts or no UUID found, keep the original filename
  return fileName;
};

export const getLastChannelMessage = (message: BaseMessage): string => {
  if (!message) return null;
  if (message.isFileMessage()) {
    try {
      if (!(message as FileMessage).plainUrl) return message.name;
      const validUrl = new URL((message as FileMessage).plainUrl);
      const segments = validUrl.pathname.split('/');

      return removeUUIDFromFilename(segments[segments.length - 1]);
    } catch (error) {
      return null;
    }
  }

  return (message as UserMessage).message ?? null;
};

export const getLastChannelMessageRelativeDate = (
  message: BaseMessage,
): string => {
  if (!message) return null;

  const createdDate = (message as UserMessage).createdAt;

  if (createdDate) {
    const incomingDate = DateTime.fromMillis(createdDate);
    const today = DateTime.now();

    if (
      [
        today.toLocaleString(),
        today.minus({ days: 1 }).toLocaleString(),
      ].includes(incomingDate.toLocaleString())
    ) {
      return incomingDate.toRelativeCalendar({ unit: 'days' });
    }

    if (
      [
        today.minus({ days: 2 }).toLocaleString(),
        today.minus({ days: 3 }).toLocaleString(),
        today.minus({ days: 4 }).toLocaleString(),
        today.minus({ days: 5 }).toLocaleString(),
        today.minus({ days: 6 }).toLocaleString(),
        today.minus({ days: 7 }).toLocaleString(),
      ].includes(incomingDate.toLocaleString())
    ) {
      return incomingDate.toFormat('ccc');
    }

    return incomingDate.toFormat('LL/dd/yy');
  }

  return null;
};

export const getChannelAvatarName = (channelTitle: string): string => {
  if (!channelTitle) return '';

  return channelTitle
    .split(',')
    .join('')
    .split('+')
    .map((segment) => segment.trim())
    .join(' ')
    .trim();
};

export const splitChannels = (
  channels: GroupChannel[],
  currentUserId: string,
): SplitGroupChannels => {
  const myChannels = [];
  const readOnlyChannels = [];

  (channels ?? []).forEach((channel) => {
    if (
      ChatUtil.isChannelMember(
        (channel.members ?? []).map((item) => item.userId),
        currentUserId,
      )
    ) {
      myChannels.push(channel);
    } else {
      readOnlyChannels.push(channel);
    }
  });

  return {
    myChannels,
    readOnlyChannels,
  };
};

export const sortMessagesByCreatedAt = (
  firstMessage: ChatMessage,
  secondMessage: ChatMessage,
): number => {
  if (
    (!firstMessage || !firstMessage.createdAt) &&
    (!secondMessage || !secondMessage.createdAt)
  )
    return 0;
  if ((!firstMessage || !firstMessage.createdAt) && secondMessage) return 1;
  if (firstMessage && (!secondMessage || !secondMessage.createdAt)) return -1;

  return secondMessage.createdAt - firstMessage.createdAt;
};

/**
 *
 * @param chatMessages: List of channel messages
 * @param messageId: To be used when removing parent if replyId is null
 * @param replyId (optional): To be used only when reply is to be removed
 *
 * Algorithm (logic):
 * - Check if the removed item is parent message or a reply
 * - If it's parent message (meaning reply is null / undefined) go ahead and mark parent message as removed.
 * - If reply is removed, trace its parent message and just mark that specific reply as removed
 */
export const markMessageAsRemoved = async (
  channel: GroupChannel,
  chatMessages: ChatMessage[],
  messageId: number,
  replyId?: number,
) => {
  if (
    !messageId ||
    messageId === 0 ||
    !chatMessages ||
    chatMessages.length === 0
  )
    return null;

  const message = chatMessages?.find((m) => m?.messageId === messageId);
  const sbMessage = message?.sbMessage;

  try {
    await channel.deleteMessage(sbMessage);
  } catch (err) {
    console.log(err);
  }

  const messageIndex: number = chatMessages?.findIndex(
    (message) => message.messageId === messageId,
  );

  if (messageIndex > -1) {
    const message = { ...chatMessages[messageIndex] };

    if (replyId) {
      const replyIndex = (message.thread ?? []).findIndex(
        (reply) => reply.messageId === replyId,
      );

      if (replyIndex > -1) {
        message.thread.splice(replyIndex, 1, {
          ...message.thread[replyIndex],
          isRemoved: true,
        });
      }
    } else {
      message.isRemoved = true;
    }

    if (message.isRemoved || (message.thread ?? []).some((x) => x.isRemoved))
      return { message, messageIndex };
  }

  return null;
};

export const getFileAttachmentErrorMessage = (
  isFileValid: boolean,
  fileStatus: string,
  defaultMessage: string,
) => {
  if (fileStatus === S3FileStatus.NEW) {
    return MESSAGE_FILE_PROCESSING;
  }

  return isEmpty(fileStatus) || isFileValid
    ? defaultMessage
    : MESSAGE_INVALID_FILE;
};

export const getUserTypeFromChatUser = (user?: User): EquipUserType => {
  if (user) {
    const memberMetaData = user.metaData as { userType: UserRoles };
    if (memberMetaData) {
      if (memberMetaData.userType === UserRoles.PROVIDER) {
        return 'provider';
      }

      if (
        [UserRoles.PROXY, UserRoles.SUPPORT].includes(memberMetaData.userType)
      ) {
        return 'support';
      }

      if (memberMetaData.userType === UserRoles.PATIENT) {
        return 'patient';
      }
    }
  }

  return undefined;
};

export const getMentionsBoxReplyPlacholderText = (
  isThread: boolean,
  isMessagesPocEnabled?: boolean,
) => {
  if (isThread && isMessagesPocEnabled)
    return MENTION_PEOPLE_TO_NOTIFY_THEM_OF_YOUR_MESSAGE;
  else if (isThread && !isMessagesPocEnabled) {
    return DEFAULT_REPLY_PLACEHOLDER;
  } else {
    return TYPE_YOUR_MESSAGE_TO_THE_GROUP_HERE;
  }
};

export default ChatUtil;

export const isActiveTreatmentStatus = (status?: string) =>
  status === PatientUniversalStatusCode.IN_TREATMENT ||
  status === PatientUniversalStatusCode.PAUSED;
